import { IonButton, IonIcon, IonModal } from '@ionic/react';
import { RootState } from 'core/store';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Logo from 'assets/icons/HK-logo.svg';
import 'core/layout/cookie-consent/CookieConsent.scss';
import { updateCookieConsent } from 'core/services/UserActions';
import {
  COOKIE_CONSENT_COOLDOWN_SECONDS,
  COOKIE_CONSENT_DECLINED_AT_KEY
} from 'core/constants';
import { gaBtnClickEvent, gaToggle } from 'core/util';

interface CookieConsentProps {
  // When provided (true or false), this instance is manually controlled by
  // its parent (e.g. the Account page's "View More" link) and must not also
  // independently auto-gate — otherwise it and the always-mounted App-level
  // instance can both pop open at once from the same Redux state change.
  forceOpen?: boolean;
  onClose?: () => void;
  onDidDismiss?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
  forceOpen,
  onClose,
  onDidDismiss
}) => {
  const manuallyControlled = forceOpen !== undefined;
  const { id, cookieConsent, impersonating } = useSelector(
    (state: RootState) => state.user
  );
  const { loggedIn } = useSelector((state: RootState) => state.login);
  const dispatch = useDispatch();

  const [dismissed, setDismissed] = useState(false);

  // The stored value is scoped to the userId that declined — a bare
  // timestamp would survive a logout, so a different account signing in on
  // the same device could inherit this account's cooldown and have its own
  // required prompt suppressed. Returns null if there's nothing stored, it's
  // unparsable, or it belongs to a different account than `id`.
  const getDeclinedAtForCurrentUser = useCallback((): number | null => {
    const raw = localStorage.getItem(COOKIE_CONSENT_DECLINED_AT_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed.userId !== id) return null;
      return typeof parsed.declinedAt === 'number' ? parsed.declinedAt : null;
    } catch {
      return null;
    }
  }, [id]);

  // The decline cooldown is only in effect when a positive cooldown is configured.
  // With cooldown <= 0 (env var unset/empty/0), there is no time-based re-prompting.
  const declineCooldownActive = (): boolean => {
    if (COOKIE_CONSENT_COOLDOWN_SECONDS <= 0) return false;
    const declinedAt = getDeclinedAtForCurrentUser();
    if (declinedAt === null) return false;
    const elapsed = (Date.now() - declinedAt) / 1000;
    return elapsed < COOKIE_CONSENT_COOLDOWN_SECONDS;
  };

  // While a decline cooldown is active, schedule a re-render for when it expires
  // so the modal re-appears without requiring navigation. Runs both after an
  // in-session decline and on a fresh mount where the cooldown is still active.
  // No-op when cooldown <= 0 (re-prompting disabled).
  useEffect(() => {
    if (manuallyControlled || COOKIE_CONSENT_COOLDOWN_SECONDS <= 0) return;
    const declinedAt = getDeclinedAtForCurrentUser();
    if (declinedAt === null) return;
    const deadlineMs = declinedAt + COOKIE_CONSENT_COOLDOWN_SECONDS * 1000;
    if (deadlineMs <= Date.now()) return;
    let timer: ReturnType<typeof setTimeout>;
    // setTimeout's delay is a 32-bit int (~24.8 day max); a cooldown longer
    // than that would otherwise get clamped and fire early. Re-check in
    // bounded chunks and reschedule from the remaining time each time
    // instead of passing the full delay in one call.
    const scheduleNextCheck = () => {
      const remainingMs = deadlineMs - Date.now();
      if (remainingMs <= 0) {
        setDismissed(false);
        return;
      }
      const MAX_TIMEOUT_MS = 24 * 60 * 60 * 1000;
      timer = setTimeout(
        scheduleNextCheck,
        Math.min(remainingMs, MAX_TIMEOUT_MS)
      );
    };
    scheduleNextCheck();
    return () => clearTimeout(timer);
  }, [
    dismissed,
    manuallyControlled,
    cookieConsent,
    loggedIn,
    getDeclinedAtForCurrentUser
  ]);

  // Whether the user has already answered the prompt. The API returns:
  //   null  -> never answered (still needs to be asked)
  //   true  -> accepted
  //   false -> declined
  const hasAnswered = cookieConsent === true || cookieConsent === false;

  // Gate logic:
  // - Accepted users (cookieConsent === true) are never re-prompted.
  // - When no cooldown is configured (<= 0): once the user has answered at all
  //   (accepted OR declined), never prompt again — only null (never answered) shows it.
  // - When a positive cooldown is configured: a declined user is re-prompted once the
  //   cooldown elapses (declineCooldownActive() gates that window).
  const shouldPrompt =
    COOKIE_CONSENT_COOLDOWN_SECONDS <= 0
      ? !hasAnswered
      : cookieConsent !== true && !declineCooldownActive();

  const showGate = manuallyControlled
    ? forceOpen
    : id !== null &&
      loggedIn === true &&
      impersonating === false &&
      !dismissed &&
      shouldPrompt;

  const openPrivacyPolicy = () => {
    window.open('https://homekeep.com/privacy-policy/', '_blank');
  };

  const accept = () => {
    gaBtnClickEvent('accept_cookie_consent');
    // updateCookieConsent handles the optimistic Redux update and clears the
    // decline-cooldown timestamp itself — see its comment for why that must
    // be centralized rather than duplicated here.
    dispatch(updateCookieConsent(true));
    gaToggle(true);
    setDismissed(true);
    onClose?.();
  };

  const decline = () => {
    gaBtnClickEvent('decline_cookie_consent');
    dispatch(updateCookieConsent(false));
    gaToggle(false);
    setDismissed(true);
    onClose?.();
  };

  return (
    <IonModal
      className="hk-cookie-consent-modal"
      backdropDismiss={false}
      isOpen={showGate}
      aria-labelledby="hk-cookie-consent-title"
      onIonModalDidDismiss={onDidDismiss}
    >
      <div className="hk-cookie-consent">
        <div className="hk-cookie-consent-content">
          <div className="hk-cookie-consent-header">
            <div className="hk-cookie-consent-header-title">
              <IonIcon
                icon={Logo}
                className="hk-cookie-consent-header-title-icon"
              />
              <div
                id="hk-cookie-consent-title"
                className="hk-cookie-consent-header-title-text"
              >
                We value your privacy
              </div>
              <div className="hk-cookie-consent-header-title-subtext">
                We use cookies on our website to give you the most relevant
                experience by remembering your preferences and your repeat
                visits. By clicking "<strong>Accept</strong>", you consent to
                the use of <strong>ALL</strong> cookies.
              </div>
            </div>
          </div>
          <div className="hk-cookie-consent-body-text">
            Read more in our{' '}
            <a color="primary" onClick={() => openPrivacyPolicy()}>
              Privacy Policy
            </a>
            .
          </div>
        </div>
        <div className="hk-cookie-consent-footer">
          <IonButton
            size="small"
            color="primary"
            strong={true}
            onClick={() => accept()}
          >
            Accept
          </IonButton>
          <IonButton
            size="small"
            color="white"
            strong={true}
            onClick={() => decline()}
          >
            Decline
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default CookieConsent;
